import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '../hooks/useFormatting';
import { PoliticianData } from '../types';
import './TopPerformers.css';

interface TopPerformersProps {
  topPerformers: PoliticianData[];
  onSelect: (politician: PoliticianData) => void;
  selectedId?: string;
  topCount: number;
  onTopCountChange: (count: number) => void;
}

const TopPerformers: React.FC<TopPerformersProps> = ({
  topPerformers,
  onSelect,
  selectedId,
  topCount,
  onTopCountChange,
}) => {
  const { t } = useTranslation();
  const { formatCurrency, formatPercentage } = useFormatting();

  const formatReturn = (value: number) => {
    const returnPct = ((value - 10000) / 10000) * 100;
    return formatPercentage(returnPct);
  };

  const renderPerformerCard = (
    performer: PoliticianData,
    index: number
  ) => {
    return (
      <button
        key={performer.id}
        className={`performer-card top ${selectedId === performer.id ? 'selected' : ''}`}
        onClick={() => onSelect(performer)}
        style={{
          '--accent-color': performer.color,
          animationDelay: `${index * 0.05}s`,
        } as React.CSSProperties}
      >
        <div className="performer-rank top">#{index + 1}</div>

        <div className="performer-avatar">
          <img
            src={`/images/${performer.id}.jpg`}
            alt={performer.name}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/images/placeholder.png';
            }}
          />
          <div
            className="avatar-ring"
            style={{ borderColor: performer.color }}
          />
        </div>

        <div className="performer-info">
          <span className="performer-name">{performer.name}</span>
          <span className="performer-meta">
            {performer.party === 'Democrat' ? 'D' : 'R'}-{performer.state}
          </span>
        </div>

        <div className="performer-stats">
          <span className="performer-value">{formatCurrency(performer.currentValue)}</span>
          <span
            className={`performer-return ${
              performer.currentValue >= 10000 ? 'positive' : 'negative'
            }`}
          >
            {formatReturn(performer.currentValue)}
          </span>
        </div>

        <div
          className="performer-indicator"
          style={{ backgroundColor: performer.color }}
        />
      </button>
    );
  };

  return (
    <div className="top-performers">
      <div className="performers-section">
        <div className="performers-header">
          <div className="performers-label">
            <span className="label-icon top">&#9650;</span>
            {t('politician:topPerformers')}
          </div>
          <select
            className="top-count-selector"
            value={topCount}
            onChange={(e) => onTopCountChange(Number(e.target.value))}
          >
            <option value={5}>Top 5</option>
            <option value={10}>Top 10</option>
            <option value={20}>Top 20</option>
          </select>
        </div>
        <div className="performers-grid">
          {topPerformers.map((performer, index) =>
            renderPerformerCard(performer, index)
          )}
        </div>
      </div>
    </div>
  );
};

export default TopPerformers;
